import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent, CartAddEvent, DiscountUpdateEvent } from '@theme/events';
import { cartSync, morphCartSection, leave, tick } from '@theme/cart-sync';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Timestamp of the most recent cart-mutating event. A server section render
 * that STARTED before this instant reflects the pre-change cart — morphing it
 * in would visibly yank counts/rows backwards, so such responses are dropped.
 */
let lastCartChangeAt = 0;

/**
 * The cart's line items (cart page and cart drawer).
 *
 * Quantity edits and removals are applied to the DOM the moment they happen
 * (the input shows the new number, a removed row collapses) and handed to
 * cart-sync, which batches them into as few requests as possible and renders
 * the settled state through an animated morph. Nothing is locked while a
 * request is in flight: the visitor can keep tapping.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener('cart-sync:error', this.#onSyncError);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.removeEventListener('cart-sync:error', this.#onSyncError);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * bfcache restore: the rows are a snapshot from before the user navigated
   * away — if the cart changed since (e.g. edits on the cart page), the rows
   * are stale. Re-render from the server.
   * @param {PageTransitionEvent} event - The page show event.
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshFromServer();
  };

  /**
   * A quantity selector inside this component changed. Lines are addressed
   * by variant (see cart-sync.js for why not by line number or key).
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    const variantId = event.target.dataset.variantId;
    const input = event.target.querySelector('input[data-cart-line]');
    if (!variantId || !(input instanceof HTMLInputElement)) return;

    const { quantity } = event.detail;
    // `defaultValue` mirrors the `value` attribute, which the morph keeps at
    // the last quantity the server confirmed for this line; cart-sync knows
    // better while one of its own requests is in flight.
    const from = Number(input.defaultValue) || 0;

    const row = input.closest('[data-key]');
    lastCartChangeAt = performance.now();

    if (row instanceof HTMLElement) {
      if (quantity === 0) {
        this.#removeRow(row, from);
      } else {
        row.setAttribute('data-pending', '');
        this.#dismissUndo(variantId, true);
      }
    }
    this.setAttribute('data-cart-pending', '');

    cartSync.setVariant(variantId, quantity, from);
  };

  /**
   * The "removed — undo" notices, by variant id. Each one stands exactly
   * where its row stood, at the row's height. They exist only client-side:
   * the morph keeps them (see cart-sync.js) while the settled render
   * updates the rows around them.
   * `rows` are the row elements the notice replaced, kept so that undo can
   * put them straight back.
   * @type {Map<string, {el: HTMLElement, rows: HTMLElement[], quantity: number, timer: number, startedAt: number, remaining: number, bar: Animation | null}>}
   */
  #undos = new Map();

  /**
   * Section markup for the empty cart that arrived while a notice was still
   * up. The server has already dropped the line; the visitor sees the empty
   * state once the last notice has run out (or never, if they undo).
   * @type {string | null}
   */
  #deferredEmpty = null;

  /**
   * Swap a row (and its nested component lines) for a notice of the same
   * height, in the same place, right away; the server confirms in the
   * background. `from` is the quantity the notice puts back on undo.
   * @param {HTMLElement} row
   * @param {number} from
   */
  #removeRow(row, from) {
    const rows = this.refs.cartItemRows ?? [];
    const lineKey = row.dataset.lineKey;
    const nested = rows.filter((r) => lineKey && r.dataset.parentKey === lineKey);
    const variantId = row.dataset.key?.replace(/^line-/, '') || '';
    const title = row.dataset.undoTitle || row.querySelector('.cart-items__title')?.textContent?.trim() || '';

    const notice = from > 0 && variantId ? this.#buildUndo(variantId, from, title, [row, ...nested]) : null;
    if (notice) {
      // The box takes the row's content height (the row's padding-bottom is
      // the gap to the divider, which the box gets from its own rule).
      const cs = getComputedStyle(row);
      const height = row.getBoundingClientRect().height - (parseFloat(cs.paddingBottom) || 0);
      notice.style.setProperty('--undo-height', `${Math.max(0, Math.round(height))}px`);
      row.before(notice);
      if (!prefersReducedMotion() && typeof notice.animate === 'function') {
        notice.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      }
      // A keyboard user was on the minus button that is about to go: land
      // them on the undo instead of dropping focus to the body.
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && row.contains(focused) && focused.matches(':focus-visible')) {
        /** @type {HTMLElement | null} */ (notice.querySelector('.cart-undo__button'))?.focus();
      }
      this.#resumeUndo(variantId);
    }
    // Hide now, detach next tick: this runs inside the click that hit the
    // minus button, and the drawer's click-outside check still has to see
    // that button in the DOM (a detached target reads as "outside").
    for (const r of [row, ...nested]) {
      r.setAttribute('data-removing', '');
      r.inert = true;
      r.style.display = 'none';
      setTimeout(() => r.remove(), 0);
    }
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {string} title
   * @param {HTMLElement[]} rows
   * @returns {HTMLElement}
   */
  #buildUndo(variantId, quantity, title, rows) {
    this.#dismissUndo(variantId, true);
    const wrapper = this.querySelector('.cart-items__wrapper');
    const duration = Number(wrapper?.getAttribute('data-undo-ms')) || 6000;
    const text = (wrapper?.getAttribute('data-undo-text') || '__TITLE__').replace('__TITLE__', escapeHtml(title));
    const label = escapeHtml(wrapper?.getAttribute('data-undo-label') || 'Undo');

    const el = document.createElement('tr');
    el.className = 'cart-undo__item';
    el.setAttribute('data-cart-undo-item', variantId);
    el.setAttribute('role', 'row');
    el.innerHTML = `
      <td class="cart-undo__cell" colspan="4" role="cell">
        <div class="cart-undo__box" role="status">
          <p class="cart-undo__text">${text}</p>
          <button type="button" class="cart-undo__button">${label}</button>
          <span class="cart-undo__bar" aria-hidden="true"><span class="cart-undo__bar-fill"></span></span>
        </div>
      </td>
    `;

    this.#undos.set(variantId, { el, rows, quantity, timer: 0, startedAt: 0, remaining: duration, bar: null });

    el.querySelector('.cart-undo__button')?.addEventListener('click', () => this.#undo(variantId));
    // Hovering or focusing the notice holds the clock: nobody should lose the
    // undo while reading it.
    el.addEventListener('pointerenter', () => this.#pauseUndo(variantId));
    el.addEventListener('pointerleave', () => this.#resumeUndo(variantId));
    el.addEventListener('focusin', (e) => {
      if (e.target instanceof Element && e.target.matches(':focus-visible')) this.#pauseUndo(variantId);
    });
    el.addEventListener('focusout', () => this.#resumeUndo(variantId));
    return el;
  }

  /** @param {string} variantId */
  #pauseUndo(variantId) {
    const entry = this.#undos.get(variantId);
    if (!entry || !entry.timer) return;
    clearTimeout(entry.timer);
    entry.timer = 0;
    entry.remaining = Math.max(0, entry.remaining - (performance.now() - entry.startedAt));
    entry.bar?.pause();
  }

  /**
   * (Re)start the clock and the bar from what is left.
   * @param {string} variantId
   */
  #resumeUndo(variantId) {
    const entry = this.#undos.get(variantId);
    if (!entry || entry.timer) return;
    if (entry.remaining <= 0) {
      this.#expireUndo(variantId);
      return;
    }
    entry.startedAt = performance.now();
    entry.timer = window.setTimeout(() => this.#expireUndo(variantId), entry.remaining);

    const fill = entry.el.querySelector('.cart-undo__bar-fill');
    if (fill instanceof HTMLElement && typeof fill.animate === 'function') {
      const total = Number(this.querySelector('.cart-items__wrapper')?.getAttribute('data-undo-ms')) || 6000;
      if (entry.bar && entry.bar.playState === 'paused') {
        entry.bar.play();
      } else {
        entry.bar?.cancel();
        entry.bar = fill.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], {
          duration: total,
          easing: 'linear',
          fill: 'forwards',
        });
        entry.bar.currentTime = total - entry.remaining;
      }
    }
  }

  /**
   * Put the line back.
   * @param {string} variantId
   */
  #undo(variantId) {
    const entry = this.#undos.get(variantId);
    if (!entry) return;
    // The line comes back: the empty state that was waiting is moot.
    this.#deferredEmpty = null;
    // The row itself goes straight back where the notice stood, with its
    // quantity and price, dimmed as pending until the server confirms — the
    // same as a quantity tap. The settled render then matches it by key, so
    // nothing re-enters or flashes. Nobody waits a round trip to see it.
    for (const r of entry.rows) {
      r.removeAttribute('data-removing');
      r.removeAttribute('aria-hidden');
      r.inert = false;
      r.style.display = '';
      r.style.overflow = '';
      r.style.pointerEvents = '';
      r.setAttribute('data-pending', '');
      entry.el.before(r);
      if (!prefersReducedMotion() && typeof r.animate === 'function') {
        r.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      }
    }
    this.#dismissUndo(variantId, true);
    lastCartChangeAt = performance.now();
    this.setAttribute('data-cart-pending', '');
    cartSync.setVariant(variantId, entry.quantity, 0);
  }

  /**
   * The bar ran out. If this was the last notice and the empty cart has
   * been waiting behind it, show it now, with a soft fade.
   * @param {string} variantId
   */
  #expireUndo(variantId) {
    const held = this.#deferredEmpty;
    if (held && this.#undos.size === 1 && this.#undos.has(variantId)) {
      this.#dismissUndo(variantId, true);
      this.#deferredEmpty = null;
      this.#render(held);
      if (!prefersReducedMotion() && typeof this.animate === 'function') {
        this.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
      }
      return;
    }
    this.#dismissUndo(variantId);
  }

  /**
   * @param {string} variantId
   * @param {boolean} [instant] - no exit animation
   */
  #dismissUndo(variantId, instant = false) {
    const entry = this.#undos.get(variantId);
    if (!entry) return;
    this.#undos.delete(variantId);
    clearTimeout(entry.timer);
    entry.bar?.cancel();
    if (instant || !entry.el.isConnected) {
      // Hide now, detach next tick: an undo click is still bubbling, and the
      // drawer's click-outside check reads a detached target as "outside".
      entry.el.style.display = 'none';
      setTimeout(() => entry.el.remove(), 0);
    } else {
      leave(entry.el);
    }
  }

  /**
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#refreshFromServer();
      return;
    }

    // Any cart event marks a state change: server responses fetched before
    // this instant are stale relative to it.
    lastCartChangeAt = performance.now();

    // Optimistic events fire BEFORE the server processed the change — a
    // render now would fetch the PRE-change cart. The settled event follows.
    // The count in the title / drawer heading moves with the tap like the
    // header badge does; the settled render sets it straight.
    if (event.detail.data?.optimistic) {
      const delta = Number(event.detail.data.itemCount) || 0;
      for (const el of this.querySelectorAll('.cart-bubble__text-count')) {
        if (!(el instanceof HTMLElement)) continue;
        const next = Math.max(0, (parseInt(el.textContent || '0', 10) || 0) + delta);
        el.textContent = String(next);
        tick(el);
      }
      return;
    }

    const html = event.detail.data?.sections?.[this.sectionId];
    if (html) {
      this.#render(html);
    } else {
      this.#refreshFromServer();
    }
  };

  /**
   * The server refused a change on one of these lines: the reconciling
   * render already restored the truth, this only says why.
   * @param {CustomEvent<{ids: string[], message: string}>} event
   */
  #onSyncError = (event) => {
    const { ids = [], message } = event.detail || {};
    for (const id of ids) {
      const row = this.querySelector(`[data-key="line-${CSS.escape(String(id))}"]`);
      if (!(row instanceof HTMLElement)) continue;
      const text = row.querySelector('.cart-item__error-text');
      const container = row.querySelector('.cart-items__error');
      if (text) text.textContent = message;
      container?.classList.remove('hidden');
    }
  };

  /**
   * Morph new section markup in and re-apply what the visitor is still
   * waiting on (a quantity typed while the request was in flight).
   * @param {string} html
   */
  #render(html) {
    // The last line went and the server answered with the empty cart. A
    // notice is still up, standing where the row was — hold the empty state
    // until it has run out (#expireUndo), or drop it if the visitor undoes.
    // The server cart is already empty, so checkout and other tabs are right.
    if (this.#undos.size && !/class="[^"]*\bcart-items__table-row\b/.test(html)) {
      this.#deferredEmpty = html;
      return;
    }
    this.#deferredEmpty = null;
    try {
      morphCartSection(this.sectionId, html);
    } catch (error) {
      console.error(error);
      return;
    }

    for (const selector of this.querySelectorAll('cart-quantity-selector-component[data-variant-id]')) {
      if (!(selector instanceof HTMLElement)) continue;
      const input = selector.querySelector('input[data-cart-line]');
      if (!(input instanceof HTMLInputElement)) continue;
      const variantId = selector.dataset.variantId || '';
      const desired = cartSync.desiredQuantity(variantId);
      if (desired !== undefined && desired > 0) input.value = String(desired);
      if (cartSync.isPending(variantId)) {
        input.closest('[data-key]')?.setAttribute('data-pending', '');
      } else {
        // The morph leaves the `value` attribute alone when the live value
        // already matches the server's (the optimistic number was right).
        // Pin it anyway: the next edit reads its starting point from here.
        input.defaultValue = input.value;
      }
    }
    if (cartSync.busy) this.setAttribute('data-cart-pending', '');
    // A notice whose surroundings were replaced wholesale is gone with them.
    for (const [variantId, entry] of this.#undos) {
      if (!entry.el.isConnected) this.#dismissUndo(variantId, true);
    }

    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /** @type {number} Monotonic token — a newer refresh supersedes older ones. */
  #refreshSeq = 0;

  /**
   * Re-renders this section from the server, DROPPING the response if the
   * cart changed while the request was in flight (the change's own settled
   * event triggers the next render, so a fresh one always follows).
   */
  async #refreshFromServer() {
    const startedAt = performance.now();
    const seq = ++this.#refreshSeq;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId.replace(/^shopify-section-/, ''));
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const html = await response.text();
      if (seq !== this.#refreshSeq) return; // superseded by a newer refresh
      if (lastCartChangeAt > startedAt) return; // cart changed mid-flight — response is stale
      if (cartSync.busy) return; // its settled event renders next
      this.#render(html);
    } catch {
      // keep the current DOM on failure
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
